/**
 * Logs API
 *
 * API endpoints for logs and log filtering operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { companyApi } from "../company/company.api";
import { usersApi } from "../users/users.api";
import type { FilterPagination, ListDriverLogs, ListLogs, LogDriver, LogFilters, Objects, Operations } from "./logs.types";
import { transformResponseToArr } from "./logs.utils";

/**
 * Logs API endpoints
 * Provides query operations for logs and log filtering
 */
export const logsApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get log objects (types of objects that can be logged)
     * @returns Array of object types
     */
    getObjects: build.query<Objects["raw"][], void, Objects["result"]>({
      query: () => "/logs/objects",
      transformResponse: (response) => transformResponseToArr(response) as Objects["raw"][],
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando objetos...",
          success: "Objetos carregados com sucesso!",
          error: "Erro ao carregar objetos."
        });
      }
    }),

    /**
     * Get log operations (types of operations that can be logged)
     * @returns Array of operation types
     */
    getOperations: build.query<Operations["raw"][], void, Operations["result"]>({
      query: () => "/logs/operations",
      transformResponse: (response) => transformResponseToArr(response) as Operations["raw"][],
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando operações...",
          success: "Operações carregadas com sucesso!",
          error: "Erro ao carregar operações."
        });
      }
    }),

    /**
     * List logs with infinite scroll pagination
     * @param params - Filter parameters for logs
     * @returns Paginated list of logs
     */
    listLogs: build.infiniteQuery<ListLogs, { params?: FilterPagination } | void, FilterPagination>({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 0,
          pageSize: 50,
          startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
          endDate: dayjs().format("YYYY-MM-DD")
        },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params } = {}, pageParam }) => ({
        url: `/logs`,
        params: Object.assign({}, params, pageParam)
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando logs...",
          success: "Logs carregados com sucesso!",
          error: "Erro ao carregar logs."
        });
      }
    }),

    /**
     * Get logs for a specific driver
     * @param id - Driver ID
     * @param params - Optional date range filter
     * @returns Array of driver logs
     */
    getLogDriver: build.query<LogDriver[], { id: number; params?: Partial<Pick<FilterPagination, "startDate" | "endDate">> }>({
      query: ({ id, params }) => ({
        url: `/log-driver/${id}`,
        params
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando logs do motorista...",
          success: "Logs do motorista carregados com sucesso!",
          error: "Erro ao carregar logs do motorista."
        });
      }
    }),

    /**
     * List driver logs with infinite scroll pagination
     * @param id - Driver ID
     * @param params - Date range filter
     * @returns Paginated list of driver logs
     */
    listLogDriver: build.infiniteQuery<ListDriverLogs, { id: number; params: { startDate: string; endDate: string } }, App.Filters.Pagination<LogDriver>, ListDriverLogs>({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 100, sortDirection: "asc", sortField: "createdAt" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { id, params }, pageParam }) => ({
        url: `/log-driver/${id}`,
        params: Object.assign({}, pageParam, params)
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando logs do motorista...",
          success: "Logs do motorista carregados com sucesso!",
          error: "Erro ao carregar logs do motorista."
        });
      }
    }),

    /**
     * Get combined log filters (users, operations, objects, companies)
     * This endpoint aggregates data from multiple sources
     * @returns Combined filter options
     */
    getLogFilters: build.query<LogFilters, void>({
      queryFn: async () => ({
        data: {
          users: [],
          operations: [],
          objects: [],
          companies: []
        }
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const usersPromise = dispatch(usersApi.endpoints.getUsers.initiate()).unwrap();
        const companyPromise = dispatch(companyApi.endpoints.getCompanies.initiate()).unwrap();
        const getObjects = dispatch(logsApi.endpoints.getObjects.initiate()).unwrap();
        const getOperations = dispatch(logsApi.endpoints.getOperations.initiate()).unwrap();

        const promises = [queryFulfilled, getObjects, getOperations, companyPromise, usersPromise] as const;
        const [, objectsResult, operationsResult, companyResult, usersResult] = await Promise.all(promises);

        dispatch(
          logsApi.util.updateQueryData("getLogFilters", undefined, (draft) => {
            Object.assign<object, typeof draft>(draft, {
              companies: companyResult ?? [],
              users: usersResult ?? [],
              objects: objectsResult ?? [],
              operations: operationsResult ?? []
            });
          })
        );
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
logsApi.enhanceEndpoints({
  addTagTypes: ["Objects", "Operations", "Filters", "Logs", "DriverLogs"],
  endpoints: {
    getObjects: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Objects" as const, id })), { type: "Objects", id: "LIST" }] : [{ type: "Objects", id: "LIST" }])
    },
    getOperations: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "Operations" as const, id })), { type: "Operations", id: "LIST" }] : [{ type: "Operations", id: "LIST" }]
    },
    listLogs: {
      providesTags: (result) =>
        result
          ? [...result.pages.flatMap((page) => page.data.map((item) => ({ type: "Logs" as const, id: item.id }))), { type: "Logs", id: "LIST" }]
          : [{ type: "Logs", id: "LIST" }]
    },
    listLogDriver: {
      providesTags: (result) =>
        result
          ? [...result.pages.flatMap((page) => page.data.map((item) => ({ type: "DriverLogs" as const, id: item.id }))), { type: "DriverLogs", id: "LIST" }]
          : [{ type: "DriverLogs", id: "LIST" }]
    },
    getLogDriver: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "DriverLogs" as const, id })), { type: "DriverLogs", id: "LIST" }] : [{ type: "DriverLogs", id: "LIST" }]
    },
    getLogFilters: {
      providesTags: [{ type: "Filters", id: "LIST" }]
    }
  }
});
